import { RecordUtils } from "@rsm-hcd/javascript-core";
import { t } from "i18next";
import { Record } from "immutable";
import {
    BasePageAccessEntry,
    PageAccessEntrySource,
} from "models/interfaces/page-access-entry";
import moment from "moment";

type PageAccessEntryRecordData = BasePageAccessEntry & {
    pageAccessRecipientId?: number;
    notebookResourcePermissionId?: number;
    recipientGuid?: string;
    expiresOn?: Date;
};

const defaultValues: PageAccessEntryRecordData =
    RecordUtils.defaultValuesFactory({
        pageId: 0,
        email: "",
        source: PageAccessEntrySource.PageAccessRecipient,
        pageAccessRecipientId: 0,
        notebookResourcePermissionId: undefined,
        recipientGuid: undefined,
        expiresOn: undefined,
        userId: undefined,
    });

export class PageAccessEntryRecord
    extends Record(defaultValues)
    implements PageAccessEntryRecordData
{
    constructor(params?: Partial<PageAccessEntryRecordData>) {
        if (params == null) {
            params = Object.assign({}, defaultValues);
        }

        super(params);
    }

    public with(
        values: Partial<PageAccessEntryRecordData>
    ): PageAccessEntryRecord {
        return new PageAccessEntryRecord(Object.assign(this.toJS(), values));
    }

    public isPageAccessRecipient(): boolean {
        return this.source === PageAccessEntrySource.PageAccessRecipient;
    }

    public isNotebookResourcePermission(): boolean {
        return this.source === PageAccessEntrySource.NotebookResourcePermission;
    }

    public getSourceId(): number | undefined {
        if (this.isPageAccessRecipient()) {
            return this.pageAccessRecipientId;
        }

        return this.notebookResourcePermissionId;
    }

    public daysUntilAccessExpires(): number | undefined {
        if (!this.expiresOn) {
            return undefined;
        }

        const expirationDate = moment(this.expiresOn);
        const daysUntilExpiration = expirationDate.diff(moment(), "days");

        if (daysUntilExpiration < 0) {
            return 0;
        }

        return daysUntilExpiration;
    }

    public getExpirationDay(): string | undefined {
        if (!this.expiresOn) {
            return undefined;
        }

        const expirationInDays = this.daysUntilAccessExpires();

        if (expirationInDays === 1) {
            return t("managePageAccess-dayUntilAccessExpires");
        }

        if (expirationInDays === 0) {
            return t("managePageAccess-todayAccessExpires");
        }

        return t("managePageAccess-daysUntilAccessExpires", {
            count: expirationInDays,
        });
    }

    public accessExpired(): boolean {
        return moment(this.expiresOn).isBefore(moment());
    }
}
